// src/utils/discountPricing.js
//
// The discount figure a checkout page SHOWS must equal what the customer is
// actually charged. Stripe applies a promotion_code to the WHOLE session:
//   • percent_off → that percent of the real basket total (Stripe discounts each
//     line item; the parts sum exactly to percent-of-total). Currency-independent.
//   • amount_off  → the fixed amount, once, capped at the basket total, in the
//     coupon's own currency.
//
// The Cloud Function validateDiscountCode computes its `discountAmount` against
// (firstSelectedPackage.price × itemCount) — correct only when every line is the
// same package. On a mixed-package basket it is wrong, sometimes by tens of
// dollars. Every page that lets a customer pick a package per student
// (TrialEnroll, AddChildren, CourseRenewal) must price the discount with this
// helper instead, against its own real basket subtotal.
//
// This is DISPLAY arithmetic. It does not change how any charge is made — the
// checkout paths already hand Stripe the promotion code and let Stripe apply it
// to the real session; the saved-card renewal path already computes against the
// real basket server-side. This only makes the number on the page match.

const BASKET_CURRENCY = 'cad';

function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * @param {Object|null} coupon  - the `coupon` object from validateDiscountCode's
 *   response: { percent_off, amount_off, currency }. Pass null when there is no
 *   validated coupon.
 * @param {number} subtotal - the real basket total in dollars (sum of the
 *   actually-selected packages).
 * @returns {{ amount: number, wrongCurrency: boolean }}
 *   amount        - dollars to subtract, rounded to cents and capped at subtotal;
 *                   0 when nothing applies.
 *   wrongCurrency - true for an amount_off coupon whose currency isn't CAD.
 *                   Stripe refuses it at checkout, so the caller must show no
 *                   number and not forward the code.
 */
export function resolveDiscount(coupon, subtotal) {
  const sub = Number(subtotal) || 0;
  if (!coupon || sub <= 0) return { amount: 0, wrongCurrency: false };

  if (coupon.percent_off) {
    const raw = sub * (coupon.percent_off / 100);
    return { amount: Math.min(round2(raw), sub), wrongCurrency: false };
  }

  if (coupon.amount_off != null) {
    if (coupon.currency && String(coupon.currency).toLowerCase() !== BASKET_CURRENCY) {
      return { amount: 0, wrongCurrency: true };
    }
    const raw = coupon.amount_off / 100; // Stripe stores amount_off in cents
    return { amount: Math.min(round2(raw), sub), wrongCurrency: false };
  }

  return { amount: 0, wrongCurrency: false };
}
